import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { TranslationNS } from '@gokrew/models/constants';
import InfoCard from '@/components/InfoCard';
import { WorkspaceActionRow } from '@/components/WorkspaceActionRow';
import { useWorkspaceActions } from '@/hooks/useWorkspaceActions';
import {
  useCanSeeApprovals,
  useMyTeamApprovals,
} from '@/hooks/my-team/useMyTeam';
import type { WorkspaceAction } from '@/types/workspace';
import { CardLoader } from '../CardLoader';
import styles from './NeedsAttention.module.css';

export const NeedsAttention = () => {
  const { t } = useTranslation(TranslationNS.EmployeeTasks);
  const actions = useWorkspaceActions();
  const approvals = useMyTeamApprovals();
  const canApprove = useCanSeeApprovals();
  const requests: WorkspaceAction[] = (approvals.data ?? []).map((request) => ({
    key: `approval-${request.id}`,
    id: request.id,
    kind: 'approval',
    title: request.employeeName,
    due: request.startDate,
    overdue: !!request.overdue,
    request,
  }));
  const rows = [...requests, ...actions.rows];
  const loading = actions.isLoading || approvals.isLoading;
  const failed = actions.isError || approvals.isError;
  return (
    <InfoCard
      dataTour="emp-dashboard-tasks"
      className={styles.card}
      title={t('workspace.title')}
      action={
        <Link className={styles.link} to="/tasks">
          {t('workspace.view_all')} <ArrowRight aria-hidden />
        </Link>
      }
    >
      {loading && <CardLoader />}
      {failed && (
        <div role="alert" className={styles.error}>
          {t('workspace.partial_error')}{' '}
          <button
            onClick={() => {
              void actions.retry();
              if (canApprove) void approvals.refetch();
            }}
          >
            {t('tasks.retry')}
          </button>
        </div>
      )}
      {rows.slice(0, 8).map((row) => (
        <WorkspaceActionRow key={row.key} item={row} />
      ))}
      {!loading && !failed && !rows.length && (
        <p className={styles.empty}>
          <CheckCircle2 aria-hidden />
          {t('workspace.empty')}
        </p>
      )}
      {rows.length > 8 && (
        <Link className={styles.link} to="/tasks">
          {t('workspace.more', { count: rows.length - 8 })}{' '}
          <ArrowRight aria-hidden />
        </Link>
      )}
    </InfoCard>
  );
};
